import { LoadMessagesDirection, mergeMsg } from '@halo/chat-sdk';
import { Client } from '@halo/chat-sdk';
import { shallowCallback, type StateCreatorEnhance } from '@halo/zustand-enhance';
import type {
  Msg,
  LoadMessagesArgs,
  WasmSdkResultMap,
  User,
  SendMsgParams,
  SendForwardMsgParams,
} from '@halo/chat-sdk';
import type { WritableDraft } from 'immer';
import { flushSync } from 'react-dom';
import {
  compareTime,
  doExpireAnimate,
  TimeRelation,
  type DeepPartial,
  type Replace,
  type TypedAddEventListenerConfig,
} from '@halo/utils';
import type { SessionStore } from './main';

export enum MessagesChangeType {
  Init = 1,
  Add,
  Receive,
  Delete,
  Prepend,
  Append,
  UnPrepend,
  UnAppend,
}

export enum MessagesPosition {
  Top = 1,
  Bottom,
  Middle,
  Both,
}

export enum EnhancedMsgPayloadType {
  FirstMsg = -999,
  LastMsg,
  UnreadSplitMsg,
  DateSplitMsg,
  TodaySplitMsg,
}

export type EnhancedMsgTyped<
  T extends EnhancedMsgPayloadType,
  U extends Record<string, any> = Record<string, any>,
> = Replace<
  Pick<Msg, 'payload_type' | 'cli_msg_id' | 'created_at'>,
  {
    payload_type: T;
    created_at?: number;
  }
> &
  U;

export type FirstMsg = EnhancedMsgTyped<EnhancedMsgPayloadType.FirstMsg>;

export type LastMsg = EnhancedMsgTyped<EnhancedMsgPayloadType.LastMsg>;

export type UnreadSplitMsg = EnhancedMsgTyped<EnhancedMsgPayloadType.UnreadSplitMsg>;

export type DateSplitMsg = EnhancedMsgTyped<EnhancedMsgPayloadType.DateSplitMsg>;

export type TodaySplitMsg = EnhancedMsgTyped<EnhancedMsgPayloadType.TodaySplitMsg>;

export type EnhancedMsg =
  | Msg
  | FirstMsg
  | LastMsg
  | UnreadSplitMsg
  | DateSplitMsg
  | TodaySplitMsg;

export interface SetMessagesPositionOptions {
  position: MessagesPosition | undefined;
}

export interface LimitMessagesOptions {
  position: MessagesPosition.Top | MessagesPosition.Bottom;
}

export type MessagesChangeTrigger = {
  type: MessagesChangeType;
  anchorMessage?: Msg;
  toBottom?: boolean;
  requireScroll?: boolean | Msg['cli_msg_id'] | number;
  highlight?: Array<Msg['cli_msg_id']>;
};

export type SendSessionMessageParams = Partial<Pick<SendMsgParams, 'chat_id'>> &
  Omit<SendMsgParams, 'chat_id'>;

export type SendSessionForwardMessageParams = Partial<Pick<SendForwardMsgParams, 'chat_id'>> &
  Omit<SendForwardMsgParams, 'chat_id'>;

export interface MessagesStore {
  lastReadSequence: number | undefined;
  messagesChangeTrigger: MessagesChangeTrigger | undefined;
  draftMessage:
    | {
        payload?: string;
        mentionIds?: string[];
        forwardId?: string;
        forwardMsg?: Msg;
        forwardMsgUser?: User;
        isEditing?: boolean;
        replyId?: string;
        replyMsg?: Msg;
        replyMsgUser?: User;
      }
    | undefined;
  loadLoadings: {
    [MessagesPosition.Top]: boolean;
    [MessagesPosition.Bottom]: boolean;
    [MessagesPosition.Both]: boolean;
  };
  loadNoMores: {
    [MessagesPosition.Top]: boolean;
    [MessagesPosition.Bottom]: boolean;
  };
  maxLimit: number;
  loadLimit: number;
  position: MessagesPosition | undefined;
  firstMessage: Msg | undefined;
  lastMessage: Msg | undefined;
  messages: Msg[];
  enhancedMessages: EnhancedMsg[];
  limitMessagesTimer: any;
  addingAbortControllers: Record<string, AbortController>;
  msgHandlers: [() => void, () => void][];
  createMessages: (options: CreateMessagesOptions) => void;
  findMessage: (cli_msg_id: string) => {
    index: number;
    message: Msg;
  };
  setMessages: (
    handler: (
      msgs: Msg[],
      store: WritableDraft<SessionStore>
    ) => {
      messages: Msg[];
      trigger: MessagesChangeTrigger | undefined;
    }
  ) => void;
  setDraftMessage: (draftMessage: MessagesStore['draftMessage']) => void;
  sendDraftMessage: (draftMessage?: MessagesStore['draftMessage']) => Promise<void>;
  deleteDraftReplyMsgHandler: (deletedMsgId: string) => void;
  deleteDraftForwardMsgHandler: (deletedMsgId: string) => void;
  /**
   * @deprecated
   * @see sendMsg
   */
  sendMessage: (
    payload: SendSessionMessageParams['payload'],
    params?: Omit<SendSessionMessageParams, 'payload'>
  ) => Promise<null | { cli_msg_id: string }>;
  sendMsg: (params: SendSessionMessageParams) => Promise<null | { cli_msg_id: string }>;
  sendForwardMessage: (params: SendSessionForwardMessageParams) => Promise<void>;
  getMessages: (
    args: Pick<LoadMessagesArgs, 'anchor_sequence' | 'direction' | 'limit' | 'abortSignal'>
  ) => WasmSdkResultMap['loadMessages'];
  loadBorderMessages: (
    args?: Partial<LoadMessagesArgs & Pick<MessagesChangeTrigger, 'requireScroll'>>
  ) => Promise<void>;
  limitMessages: (args: LimitMessagesOptions) => () => void;
  setMessagesPosition: (options: SetMessagesPositionOptions) => void;
  addMessage: (message: Msg) => Promise<void>;
  deleteMessages: (cli_msg_ids: string[], delete_other_party: boolean) => Promise<void>;
  deleteStateMessages: (cli_msg_ids: string[]) => Promise<void>;
  scrollToMessage: (msg: Msg, highlight?: boolean) => Promise<void>;
  receiveMessages: (messages: Msg[]) => void;
  loadFirstMessage: () => Promise<Msg | undefined>;
  loadLastMessage: () => Promise<Msg | undefined>;
  updateMessages: (messages: Array<Pick<Msg, 'cli_msg_id'> & DeepPartial<Msg>>) => void;
  readMessages: (ids: string[]) => Promise<void>;
  watchPosition: (position: MessagesPosition) => void;
  watchLoadNoTopMores: (args: readonly [Msg, Msg[]]) => void;
  watchLoadNoBottomMores: (args: readonly [Msg, Msg[]]) => void;
  watchEnhancedMessages: () => void;
  watchSessionReady: (ready: boolean) => void;
  watchActivated: (activated: boolean) => void;
  watchDraftReplyId: (replyId: string) => Promise<void>;
  watchDraftForwardId: (forwardId: string) => Promise<void>;
  initMessagesWatches: () => () => void;
  clearMessages: (chat_id?: string) => Promise<void>;
}

export type CreateMessagesOptions = Partial<MessagesStore>;

export const messagesStoreCreator: StateCreatorEnhance<SessionStore> = (set, get, instance) =>
  ({
    position: MessagesPosition.Both,
    loadLoadings: {
      [MessagesPosition.Top]: false,
      [MessagesPosition.Bottom]: false,
      [MessagesPosition.Both]: false,
    },
    loadNoMores: {
      [MessagesPosition.Top]: false,
      [MessagesPosition.Bottom]: false,
    },
    messages: [],
    enhancedMessages: [],
    addingAbortControllers: {},
    msgHandlers: [],
    createMessages: (options: CreateMessagesOptions) => {
      const { maxLimit = 50, loadLimit = 20 } = options;
      const store = get();
      const { client, initMessagesWatches, effects } = store;

      initMessagesWatches();

      // TODO amo: Remove listeners when session is destroyed
      const config: TypedAddEventListenerConfig = {
        getIsReady: () => {
          return get().ready;
        },
      };

      store.msgHandlers = [
        client.addEventListener(
          'SendMessage',
          ({ detail: { chat_id, message } }) => {
            const { session, addMessage } = get();
            if (session && chat_id === session.chat_id) {
              addMessage(message);
            }
          },
          config
        ),

        client.addEventListener(
          'NewMessages',
          ({ detail: { chat_id, messages } }) => {
            const { session, receiveMessages } = get();
            if (session && chat_id === session.chat_id) {
              receiveMessages(messages);
            }
          },
          config
        ),

        client.addEventListener(
          'UpdatePartialMessage',
          ({ detail: { chat_id, ...message } }) => {
            const { session, updateMessages } = get();
            if (session && chat_id === session.chat_id) {
              updateMessages([message]);
            }
          },
          config
        ),

        client.addEventListener(
          'AtMemberReadMessage',
          ({ detail: { chat_id, cli_msg_id, user_id } }) => {
            const { session, findMessage } = get();
            if (session && chat_id === session.chat_id) {
              const { index, message } = findMessage(cli_msg_id);
              if (message) {
                set((store) => {
                  store.messages[index].at_read_list.push(user_id);
                });
              }
            }
          },
          config
        ),

        client.addEventListener(
          'DeleteMessages',
          async ({ detail: { chat_id, cli_msg_ids } }) => {
            const {
              session,
              deleteStateMessages,
              deleteDraftReplyMsgHandler,
              deleteDraftForwardMsgHandler,
            } = get();
            if (session && chat_id === session.chat_id) {
              // TODO: for delay delete animate
              await doExpireAnimate();
              deleteStateMessages(cli_msg_ids);
              cli_msg_ids.forEach((cli_msg_id) => {
                deleteDraftReplyMsgHandler(cli_msg_id);
                deleteDraftForwardMsgHandler(cli_msg_id);
              });
            }
          },
          config
        ),

        client.addEventListener(
          'ClearMessages',
          async ({ detail: { chat_id } }) => {
            const { session } = get();
            if (session && chat_id === session.chat_id) {
              // TODO: for delay clear animate
              await doExpireAnimate();
              set((store) => {
                store.messages = [];
                store.firstMessage = undefined;
                store.lastMessage = undefined;
              });
            }
          },
          config
        ),
      ];

      effects.push(() => {
        const { msgHandlers } = get();
        msgHandlers.forEach(([remove]) => {
          remove();
        });
      });

      set({
        ...options,
        maxLimit,
        loadLimit,
      });
      console.log(`4. [halo-chat] createMessages.`);
    },
    deleteDraftReplyMsgHandler: (deletedMsgId) => {
      const { draftMessage, setDraftMessage } = get();
      if (draftMessage?.replyId === deletedMsgId) {
        const payload = draftMessage?.payload;
        const isEditing = draftMessage?.isEditing;
        setDraftMessage(undefined);
        setDraftMessage({ isEditing, payload });
      }
    },
    deleteDraftForwardMsgHandler: (deletedMsgId) => {
      const { draftMessage, setDraftMessage } = get();
      if (draftMessage?.forwardId === deletedMsgId) {
        setDraftMessage(undefined);
      }
    },
    setDraftMessage: (draftMessage) => {
      set((store) => {
        store.draftMessage = draftMessage && { ...store.draftMessage, ...draftMessage };
      });
    },
    sendDraftMessage: async (draftMessage) => {
      const { draftMessage: draftMessage_, sendMsg, setDraftMessage } = get();
      draftMessage = draftMessage ?? draftMessage_;
      if (draftMessage) {
        setDraftMessage(undefined);
        await sendMsg({
          payload: draftMessage.payload,
          mentionIds: draftMessage.mentionIds,
          replyId: draftMessage.isEditing
            ? draftMessage?.replyMsg?.message_id
            : draftMessage?.replyMsg?.cli_msg_id,
          isEditing: draftMessage.isEditing,
        });
      }
    },
    sendMessage: async (payload, params) => {
      const { client, session, sendMsg } = get();
      if (client && session) {
        return sendMsg({
          payload,
          ...params,
        });
      }
      return null;
    },
    sendMsg: (params) => {
      const { client, session } = get();
      if (client && session) {
        return client.Api.messages.sendMsg({
          chat_id: params.chat_id || session.chat_id,
          ...params,
        });
      }
      return null;
    },
    sendForwardMessage: async (params) => {
      const { client, session } = get();
      if (client) {
        await client.Api.messages.sendForwardMsg({
          chat_id: params.chat_id || session.chat_id,
          ...params,
        });
      }
    },
    getMessages: ({ limit, direction, ...args }) => {
      const { client, session, loadLimit } = get();

      if (client && session) {
        const limit_ = limit ?? loadLimit;
        return client.Api.messages.loadMessages({
          chat_id: session.chat_id,
          limit: direction === LoadMessagesDirection.Center ? limit_ * 2 : limit_,
          direction,
          ...args,
        });
      }
    },
    findMessage: (id: string) => {
      // message id or cli_msg_id
      const { messages } = get();
      const index = messages.findIndex(
        (message) => message.cli_msg_id === id || message.message_id === id
      );
      return {
        index,
        message: messages[index],
      };
    },
    loadBorderMessages: async ({
      anchor_sequence,
      direction = LoadMessagesDirection.Down,
      requireScroll: requireScroll_,
      ...args
    } = {}) => {
      const store = get();
      const { client, session, messages, loadLoadings, getMessages, limitMessages } = store;
      const isPrepend = direction === LoadMessagesDirection.Up;
      const isAppend = direction === LoadMessagesDirection.Down;
      const isCenter = direction === LoadMessagesDirection.Center;

      const position = isPrepend
        ? MessagesPosition.Top
        : isAppend
          ? MessagesPosition.Bottom
          : MessagesPosition.Both;

      if (client && session && !loadLoadings[MessagesPosition.Both] && !loadLoadings[position]) {
        store.loadLoadings[position] = true;

        if (anchor_sequence === undefined) {
          if (messages.length === 0) {
            anchor_sequence = session.last_read_sequence;
          } else {
            const anchorMsg = messages.at(direction === LoadMessagesDirection.Up ? 0 : -1)!;
            anchor_sequence = [anchorMsg.sequence, anchorMsg.local_sequence];
          }
        }

        const handleLimitMessages = isCenter
          ? undefined
          : limitMessages({
              // @ts-expect-error isCenter is true
              position,
            });

        await getMessages({
          anchor_sequence,
          direction,
          ...args,
        })
          .then(({ messages, anchor_cli_msg_id }) => {
            const hasMessages = messages?.length;

            if (hasMessages) {
              const { setMessages, findMessage } = get();

              setMessages((messages_) => {
                if (isCenter) {
                  messages_ = messages;
                } else {
                  const addedMessages: Msg[] = [];

                  for (let i = 0; i < messages.length; i++) {
                    const msg = messages[i];
                    const { index } = findMessage(msg.cli_msg_id);
                    if (index === -1) {
                      addedMessages.push(msg);
                    } else {
                      messages_[index] = msg;
                    }
                  }

                  if (isPrepend) {
                    messages_.unshift(...addedMessages);
                  } else {
                    messages_.push(...addedMessages);
                  }
                }

                return {
                  messages: messages_,
                  trigger: {
                    type: isCenter
                      ? MessagesChangeType.Init
                      : isPrepend
                        ? MessagesChangeType.Prepend
                        : MessagesChangeType.Append,
                    requireScroll:
                      requireScroll_ ?? (isCenter ? (anchor_cli_msg_id ?? false) : isPrepend),
                  },
                };
              });

              handleLimitMessages?.();
            }
          })
          .finally(() => {
            store.loadLoadings[position] = false;
          });
      }
    },
    setMessages: (handler) => {
      flushSync(() => {
        set((store) => {
          const { messages, trigger } = handler([...store.messages], store);
          store.messagesChangeTrigger = trigger;
          store.messages = messages;
        });
      });
    },
    limitMessages: ({ position }) => {
      const store = get();

      clearTimeout(store.limitMessagesTimer);

      return () => {
        const limitMessagesTimer = setTimeout(() => {
          const { messages, maxLimit, setMessages } = get();
          const overflow = messages.length - maxLimit;
          if (overflow > 0) {
            const isPrepend = position === MessagesPosition.Top;
            let messages_: Msg[];
            if (isPrepend) {
              messages_ = messages.slice(0, maxLimit);
            } else {
              messages_ = messages.slice(overflow);
            }
            setMessages(() => ({
              messages: messages_,
              trigger: {
                type: isPrepend ? MessagesChangeType.UnPrepend : MessagesChangeType.UnAppend,
              },
            }));
          }
        }, 2000);

        store.limitMessagesTimer = limitMessagesTimer;
      };
    },
    setMessagesPosition: ({ position }) => {
      const { ready, activated } = get();
      if (ready && activated) {
        set({ position });
      }
    },
    addMessage: async (message) => {
      const {
        firstMessage,
        messages,
        setMessages,
        findMessage,
        limitMessages,
        getMessages,
        addingAbortControllers,
        loadLastMessage,
        loadNoMores,
      } = get();

      const noMoreBottom = loadNoMores[MessagesPosition.Bottom];
      const { index } = findMessage(message.cli_msg_id);
      const notFound = index === -1;

      let messages_ = [...messages];
      let trigger: MessagesChangeTrigger | undefined;

      const handleLimitMessages =
        notFound && noMoreBottom
          ? limitMessages({ position: MessagesPosition.Bottom })
          : undefined;

      if (notFound) {
        trigger = { type: MessagesChangeType.Add, toBottom: true };

        if (noMoreBottom) {
          messages_.push(message);
        } else {
          addingAbortControllers[message.cli_msg_id]?.abort();

          try {
            const lastMessage = await loadLastMessage();

            if (lastMessage) {
              const abortController = new AbortController();
              addingAbortControllers[message.cli_msg_id] = abortController;

              messages_ = await getMessages({
                direction: LoadMessagesDirection.Up,
                anchor_sequence: lastMessage.sequence + 1,
                abortSignal: abortController.signal,
              }).then(({ messages }) => {
                return messages;
              });
            }
          } catch (e) {
            if (e instanceof Client.AbortError) {
              messages_ = [];
            } else {
              throw e;
            }
          } finally {
            delete addingAbortControllers[message.cli_msg_id];
          }
        }
      } else {
        messages_[index] = message;
      }

      if (messages_.length) {
        setMessages((_, store) => {
          if (notFound) {
            store.lastReadSequence = undefined;
            if (noMoreBottom) {
              store.lastMessage = message;
            }

            if (!firstMessage) {
              store.firstMessage = message;
            }
          }

          return { messages: messages_, trigger };
        });

        handleLimitMessages?.();
      }
    },
    deleteMessages: async (cli_msg_ids: string[], delete_other_party: boolean) => {
      const { client, session } = get();
      if (client && session) {
        await client.Api.messages.deleteMessages({
          chat_id: session.chat_id,
          cli_msg_ids,
          delete_other_party,
        });
      }
    },
    deleteStateMessages: async (cli_msg_ids: string[]) => {
      const {
        messages,
        firstMessage,
        lastMessage,
        findMessage,
        setMessages,
        loadFirstMessage,
        loadLastMessage,
      } = get();

      const messages_ = [...messages];

      let containFirstMessage = false;
      let containLastMessage = false;

      for (let i = cli_msg_ids.length - 1; i >= 0; i--) {
        const cli_msg_id = cli_msg_ids[i];
        const { index } = findMessage(cli_msg_id);
        if (index !== -1) {
          messages_.splice(index, 1);

          if (cli_msg_id === firstMessage?.cli_msg_id) {
            containFirstMessage = true;
          } else if (cli_msg_id === lastMessage?.cli_msg_id) {
            containLastMessage = true;
          }
        }
      }

      setMessages(() => {
        return { messages: messages_, trigger: { type: MessagesChangeType.Delete } };
      });

      if (containFirstMessage) {
        await loadFirstMessage();
      }

      if (containLastMessage) {
        await loadLastMessage();
      }
    },
    clearMessages: async (chat_id?: string) => {
      const { client, session } = get();
      const chat_id_ = chat_id || session?.chat_id;
      if (client && chat_id_) {
        await client.Api.messages.clearMessages(chat_id_);
        client.dispatchEvent('ClearMessages', { chat_id: chat_id_ });
      }
    },
    scrollToMessage: async (msg: Msg, highlight?: boolean) => {
      const { setMessages, findMessage, getMessages } = get();

      const { index } = findMessage(msg.cli_msg_id);

      const trigger = {
        type: MessagesChangeType.Init,
        requireScroll: msg.cli_msg_id,
        highlight: highlight ? [msg.cli_msg_id] : undefined,
      };

      if (index === -1) {
        const messages = await getMessages({
          direction: LoadMessagesDirection.Center,
          anchor_sequence: [msg.sequence, msg.local_sequence],
        }).then(({ messages }) => {
          return messages;
        });

        setMessages(() => {
          return {
            messages,
            trigger,
          };
        });
      } else {
        set({
          messagesChangeTrigger: trigger,
        });
      }
    },
    receiveMessages: (messages) => {
      const { activated, firstMessage, setMessages, findMessage, position, limitMessages } =
        get();
      if (activated) {
        let lastMessage: Msg;
        let trigger: MessagesChangeTrigger | undefined;

        setMessages((messages_) => {
          messages.forEach((message) => {
            const { index } = findMessage(message.cli_msg_id);
            if (index === -1) {
              if (position === MessagesPosition.Bottom || position === MessagesPosition.Both) {
                messages_.push(message);
                trigger = {
                  type: MessagesChangeType.Receive,
                  toBottom: true,
                };
              }
              lastMessage = message;
            } else {
              messages_[index] = message;
            }
          });

          return { messages: messages_, trigger };
        });

        if (!firstMessage) {
          const firstMessage_ = messages[0];
          if (firstMessage_) {
            set({ firstMessage: firstMessage_ });
          }
        }

        if (lastMessage) {
          set({ lastMessage: lastMessage });
        }

        if (trigger) {
          limitMessages({ position: MessagesPosition.Bottom })();
        }
      }
    },
    loadFirstMessage: async () => {
      const { client, session } = get();
      if (client && session) {
        const firstMessage = await client.Api.messages.getFirstMessage(session.chat_id);

        set((store) => {
          store.firstMessage = firstMessage;
        });

        return firstMessage;
      }
    },
    loadLastMessage: async () => {
      const { client, session } = get();
      if (client && session) {
        const lastMessage = await client.Api.messages.getLastMessage(session.chat_id);

        set((store) => {
          store.lastMessage = lastMessage;
        });

        return lastMessage;
      }
    },
    updateMessages: (messages) => {
      const { setMessages, findMessage } = get();
      setMessages((messages_) => {
        messages.forEach((message) => {
          const { index } = findMessage(message.cli_msg_id || message.message_id);
          if (index === -1) return;
          const res = mergeMsg(messages_[index], message);
          messages_[index] = res;
        });
        return { messages: messages_, trigger: undefined };
      });
    },
    readMessages: async (ids) => {
      const { client, session } = get();
      if (client && session) {
        await client.Api.messages.sendReadReceipts(session.chat_id, ids);
      }
    },
    watchPosition(position) {
      if (position !== MessagesPosition.Middle && position !== MessagesPosition.Both) {
        const { loadBorderMessages } = get();
        loadBorderMessages({
          direction:
            position === MessagesPosition.Top
              ? LoadMessagesDirection.Up
              : position === MessagesPosition.Bottom
                ? LoadMessagesDirection.Down
                : LoadMessagesDirection.Center,
        });
      }
    },
    watchLoadNoTopMores: ([firstMessage, messages]) => {
      set((store) => {
        const isTopNoMore = !firstMessage || messages[0]?.cli_msg_id === firstMessage.cli_msg_id;
        store.loadNoMores[MessagesPosition.Top] = isTopNoMore;
      });
    },
    watchLoadNoBottomMores: ([lastMessage, messages]) => {
      set((store) => {
        const isBottomNoMore =
          !lastMessage || messages.at(-1)?.cli_msg_id === lastMessage.cli_msg_id;
        store.loadNoMores[MessagesPosition.Bottom] = isBottomNoMore;
      });
    },
    watchEnhancedMessages: () => {
      const { messages, loadNoMores, lastReadSequence, client } = get();
      let todaySplitStartMessage: EnhancedMsg | undefined;
      const enhancedMessages: EnhancedMsg[] = messages.reduce((acc, message, index, input) => {
        acc.push(message);
        const nextMessage = input[index + 1];
        if (message.created_at && nextMessage?.created_at) {
          const { daysDiff } = compareTime(message.created_at, nextMessage.created_at);
          // Diff days split
          if (daysDiff < 0) {
            acc.push(
              {
                payload_type: EnhancedMsgPayloadType.DateSplitMsg,
                cli_msg_id: `DateSplitMsg-${nextMessage.created_at}`,
                created_at: nextMessage.created_at,
              },
              {
                payload_type: EnhancedMsgPayloadType.TodaySplitMsg,
                cli_msg_id: `TodaySplitMsg-${nextMessage.created_at}`,
                created_at: nextMessage.created_at,
              }
            );
          }

          // Today minutes split
          const compareResult = compareTime(message.created_at, client?.serverTime.time);
          if (compareResult.relation === TimeRelation.TODAY) {
            if (!todaySplitStartMessage) {
              todaySplitStartMessage = message;
            }

            const { minutesDiff } = compareTime(
              todaySplitStartMessage.created_at,
              nextMessage.created_at
            );

            const isSplit = minutesDiff <= -5;

            if (isSplit) {
              acc.push({
                payload_type: EnhancedMsgPayloadType.TodaySplitMsg,
                cli_msg_id: `TodaySplitMsg-${nextMessage.created_at}`,
                created_at: nextMessage.created_at,
              });
              todaySplitStartMessage = nextMessage;
            }
          }
        }

        return acc;
      }, []);

      // New messages split
      if (lastReadSequence) {
        const insertIndex = enhancedMessages.findIndex(
          (message) => message.sequence > lastReadSequence
        );

        if (insertIndex !== -1) {
          const previousMessage = enhancedMessages[insertIndex - 1];
          if (
            !previousMessage ||
            previousMessage.payload_type !== EnhancedMsgPayloadType.DateSplitMsg
          ) {
            enhancedMessages.splice(insertIndex, 0, {
              payload_type: EnhancedMsgPayloadType.UnreadSplitMsg,
              cli_msg_id: `UnreadSplitMsg-${enhancedMessages[insertIndex].created_at}`,
              created_at: enhancedMessages[insertIndex].created_at,
            });
          }
        }
      }

      const firstMessage = messages[0];
      if (firstMessage) {
        enhancedMessages.unshift(
          {
            payload_type: EnhancedMsgPayloadType.DateSplitMsg,
            cli_msg_id: `DateSplitMsg-${firstMessage.created_at}`,
            created_at: firstMessage.created_at,
          },
          {
            payload_type: EnhancedMsgPayloadType.TodaySplitMsg,
            cli_msg_id: `TodaySplitMsg-${firstMessage.created_at}`,
            created_at: firstMessage.created_at,
          }
        );
      }

      // No more top messages
      if (loadNoMores[MessagesPosition.Top]) {
        enhancedMessages.unshift({
          payload_type: EnhancedMsgPayloadType.FirstMsg,
          cli_msg_id: 'FirstMsg',
        });
      }

      // No more bottom messages
      if (loadNoMores[MessagesPosition.Bottom]) {
        enhancedMessages.push({
          payload_type: EnhancedMsgPayloadType.LastMsg,
          cli_msg_id: 'LastMsg',
        });
      }

      set((store) => {
        store.enhancedMessages = enhancedMessages || messages;
      });
    },
    watchSessionReady: (ready) => {
      if (ready) {
        const { msgHandlers } = get();
        msgHandlers.forEach(([, ready]) => {
          ready();
        });
      }
    },
    watchActivated(activated) {
      if (activated) {
        const { session, loadFirstMessage, loadLastMessage, loadBorderMessages, messages } =
          get();

        set({ lastReadSequence: session.unread_count ? session.last_read_sequence : undefined });

        if (!messages.length || session.unread_count) {
          Promise.all([
            loadFirstMessage(),
            loadLastMessage(),
            loadBorderMessages({
              direction: LoadMessagesDirection.Center,
              anchor_sequence: session.last_read_sequence,
            }),
          ]);
        }
      }
    },
    watchDraftReplyId: async (replyId) => {
      const { effects, client, session, setDraftMessage } = get();

      effects.draftReplyMsgUser?.();

      if (replyId) {
        const message = await client.Api.messages.getMessageByCliMsgId(session.chat_id, replyId);

        if (message) {
          const result = client.Api.user.resolveUsersInfo(message.owner_id, (user) => {
            setDraftMessage({
              replyMsg: message,
              replyMsgUser: user,
            });
          });
          effects.draftReplyMsgUser = result.removeListener;
        }
      } else {
        setDraftMessage({
          replyMsg: undefined,
          replyMsgUser: undefined,
          isEditing: undefined,
        });
      }
    },
    watchDraftForwardId: async (forwardId) => {
      const { effects, client, session, setDraftMessage } = get();

      effects.draftForwardMsgUser?.();

      if (forwardId) {
        const message = await client.Api.messages.getMessageByCliMsgId(
          session.chat_id,
          forwardId
        );

        if (message) {
          const result = client.Api.user.resolveUsersInfo(message.owner_id, (user) => {
            setDraftMessage({
              forwardMsg: message,
              forwardMsgUser: user,
            });
          });
          effects.draftForwardMsgUser = result.removeListener;
        }
      } else {
        setDraftMessage({
          forwardMsg: undefined,
          forwardMsgUser: undefined,
        });
      }
    },
    initMessagesWatches: () => {
      const {
        watchPosition,
        watchActivated,
        watchLoadNoTopMores,
        watchLoadNoBottomMores,
        watchEnhancedMessages,
        watchSessionReady,
        watchDraftReplyId,
        watchDraftForwardId,
      } = get();

      instance.subscribe((store) => store.position, shallowCallback(watchPosition));
      instance.subscribe((store) => store.activated, shallowCallback(watchActivated));
      instance.subscribe(
        (store) => [store.firstMessage, store.messages] as const,
        shallowCallback(watchLoadNoTopMores)
      );
      instance.subscribe(
        (store) => [store.lastMessage, store.messages] as const,
        shallowCallback(watchLoadNoBottomMores)
      );
      instance.subscribe(
        (store) =>
          [store.messages, ...Object.values(store.loadNoMores), store.lastReadSequence] as const,
        shallowCallback(watchEnhancedMessages)
      );
      instance.subscribe((store) => store.ready, shallowCallback(watchSessionReady));
      instance.subscribe(
        (store) => store.draftMessage?.replyId,
        shallowCallback(watchDraftReplyId)
      );
      instance.subscribe(
        (store) => store.draftMessage?.forwardId,
        shallowCallback(watchDraftForwardId)
      );
    },
  }) as SessionStore;
